import {number, currency, t, date, plugName, plural, locale} from './i18n.mjs';
import {S} from './strings.mjs';
import {pointCurrent} from './power.mjs';

const STATE = {free:'free', occupied:'busy', blocked:'busy', out_of_service:'out', unknown:'unknown'};
const ORDER = ['free', 'busy', 'out', 'unknown'];
const COUNT_KEY = {free:'freeCount', busy:'busyCount', out:'outCount', unknown:'unknownCount'};
const STATE_KEY = {free:'stateFree', busy:'stateBusy', out:'out', unknown:'unknown'};
export const LIVE_MINUTES = 24 * 60;
const known = v => v !== undefined && v !== null && v !== '';

export function distance(a, b) {
  const r = Math.PI / 180, x = (b[1] - a[1]) * r * Math.cos((a[0] + b[0]) / 2 * r), y = (b[0] - a[0]) * r;
  return Math.hypot(x, y) * 6371000;
}
export function distanceText(m) {
  if (!Number.isFinite(m)) return null;
  return m < 950 ? t('distanceMeters',{n:number(Math.max(10,Math.round(m/10)*10))}) : t('distanceKilometers',{n:number(m/1000,{maximumFractionDigits:m<9950?1:0})});
}
export function ageText(iso, now) {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return null;
  const min = Math.max(0, (now - time) / 60000);
  return min < 1 ? S.ageNow : min < 60 ? S.ageMinutes(Math.round(min)) : min < 24 * 60 ? S.ageHours(Math.round(min / 60)) : S.ageDays(Math.round(min / 1440));
}
// Rounded duration: minutes below an hour, hours below a day, then days.
export function spanText(minutes) {
  const min = Math.max(0, minutes);
  return min < 60 ? plural('spanMinutes', Math.max(1, Math.round(min))) : min < 24 * 60 ? plural('spanHours', Math.round(min / 60)) : plural('spanDays', Math.round(min / 1440));
}
// Session minutes for price rules: whole hours as hours, otherwise minutes.
// 90 min stays '90 Min.'; longer odd spans read as hours and minutes ('7 Std. 20 Min.').
const sessionSpan = m => m < 60 || (m < 120 && m % 60) ? t('spanMinutes', {n: number(m)}) : m % 60 === 0 ? t('spanHours', {n: number(m / 60)})
  : t('spanHours', {n: number(Math.floor(m / 60))}) + ' ' + t('spanMinutes', {n: number(m % 60)});
const cents = eur => number(Math.round(eur * 1000) / 10, {maximumFractionDigits: 1});
// Keep values whole: a number sticks to its unit and to the word before it ("ab 30 Min.", "Zeitgebühr 24 ct/min"),
// "+ " sticks to its fee, a session fee stays one piece; lines break only between price components.
const NBSP = '\u00a0';
export const keepValues = text => text.replace(/([\d€]) /g, '$1' + NBSP).replace(/ (?=[\d–€])/g, NBSP).replace(/^\+ /, '+' + NBSP);
const glue = text => text.replaceAll(' ', NBSP);
function tone(c) { return c.free ? 'free' : c.busy ? 'busy' : c.out && !c.unknown ? 'out' : 'unknown'; }

// Price components (see w2c/prices.py): energy, session fee, time fee, idle fee, one-off fee, limits, variation.
// -> {main: 'energy · session fee' | null, extra: ['+ time fee …', …]}.
export function priceParts(price) {
  if (!price || (!price.kwh && !price.base && !(price.minute || []).length && !price.time_fee && !price.idle_fee && !price.late_fee)) return {main: null, extra: []};
  const main = [], extra = [];
  if (price.kwh) {
    const [a, b] = price.kwh;
    main.push(b === 0 ? t('freePrice') : t('ctPerKwh', {n: a === b ? cents(a) : cents(a) + '–' + cents(b)}));
  }
  if (price.base) main.push(glue(t('sessionFee', {amount: currency(price.base)})));
  const time = price.time_fee ?? (price.minute || []).map(([from, eur]) => ({from, eur}));
  for (const f of time) {
    let text = t('timeFee', {amount: t('ctPerMin', {n: cents(f.eur)})});
    if (f.from) text += ' ' + t('fromSpan', {span: sessionSpan(f.from)});
    if (f.to) text += ' ' + t('untilSpan', {span: sessionSpan(f.to)});
    if (f.cap) text += ', ' + t('capAmount', {amount: currency(f.cap)});
    if (f.time_of_day) text += ' (' + t('certainHours') + ')';
    extra.push('+ ' + text);
  }
  for (const f of price.idle_fee || []) {
    let text = t('idleFee', {amount: t('ctPerMin', {n: cents(f.eur)}), span: sessionSpan(f.from)});
    if (f.cap) text += ', ' + t('capAmount', {amount: currency(f.cap)});
    extra.push('+ ' + text);
  }
  for (const f of price.late_fee || []) extra.push('+ ' + t('lateFee', {amount: currency(f.eur), span: sessionSpan(f.from)}));
  if (price.session_min) extra.push(glue(t('sessionMin', {amount: currency(price.session_min)})));
  if (price.session_max) extra.push(glue(t('sessionMax', {amount: currency(price.session_max)})));
  for (const v of price.varies || []) extra.push(t(v === 'time_of_day' ? 'variesTime' : 'variesDuration'));
  if (!main.length && extra.length) main.push(extra.shift().replace(/^\+ /, ''));
  return {main: main.map(keepValues).join(NBSP + '· ') || null, extra: extra.map(keepValues)};
}
// Price table (v14): one row per fee, the name left and the amount right; the fee's own conditions (from minute X,
// hours, weekdays, its cap) sit small under its name. Each condition states its own timing without a separate explanation.
// Fees come from the server (w2c/prices.py 'fees'); older payloads are converted by feesOf.
const FEES = {energy: 'feeEnergy', session: 'feeSession', time: 'feeTime', idle: 'feeIdle', late: 'feeLate'};
const pad = n => String(n).padStart(2, '0');
export const clock = m => pad(Math.floor(m / 60)) + ':' + pad(m % 60);
// Weekday names from Intl (0 = Monday; 1 Jan 2024 was a Monday), so every language gets them for free.
export const dayName = d => new Intl.DateTimeFormat(locale(), {weekday: 'short', timeZone: 'UTC'}).format(new Date(Date.UTC(2024, 0, 1 + d, 12)));
export function dayRuns(days) {
  const runs = [];
  for (const d of [...days].sort((a, b) => a - b)) if (runs.length && d === runs.at(-1)[1] + 1) runs.at(-1)[1] = d; else runs.push([d, d]);
  return runs;
}
const daysText = days => dayRuns(days).map(([a, b]) => a === b ? dayName(a) : dayName(a) + '\u2060–\u2060' + dayName(b)).join(', ');
const hoursText = hours => hours.map(([a, b]) => t('hoursRange', {from: clock(a), to: clock(b)})).join(', ');
// Older payloads (v12/v13 components or legacy kwh/minute/base) -> fee rows; a missing time window is one hint only.
export function feesOf(price) {
  if (!price) return [];
  if (Array.isArray(price.fees)) return price.fees;
  const fees = [], varies = price.varies || [];
  if (price.kwh) {
    const [a, b] = price.kwh;
    fees.push({k: 'energy', eur: a, ...(a !== b ? {eur_max: b, varies: varies.includes('duration') && !varies.includes('time_of_day') ? 'duration' : 'time'} : {})});
  }
  if (price.base) fees.push({k: 'session', eur: price.base});
  for (const f of price.time_fee ?? (price.minute || []).map(([from, eur]) => ({from, eur})))
    fees.push({k: 'time', eur: f.eur, ...(f.from ? {from: f.from} : {}), ...(f.to ? {to: f.to} : {}), ...(f.cap ? {cap: f.cap} : {}), ...(f.time_of_day ? {some_hours: true} : {})});
  for (const f of price.idle_fee || []) fees.push({k: 'idle', eur: f.eur, ...(f.from ? {from: f.from} : {}), ...(f.cap ? {cap: f.cap} : {})});
  for (const f of price.late_fee || []) fees.push({k: 'late', eur: f.eur, ...(f.from ? {from: f.from} : {})});
  return fees;
}
function feeAmount(f) {
  const n = v => cents(v), money = v => currency(v);
  if (f.k === 'energy') return f.eur === 0 && !f.eur_max ? t('freePrice') : (f.add ? '+' : '') + t('ctPerKwh', {n: f.eur_max ? n(f.eur) + '–' + n(f.eur_max) : n(f.eur)});
  if (f.k === 'time' || f.k === 'idle') return t('ctPerMin', {n: f.eur_max ? n(f.eur) + '–' + n(f.eur_max) : n(f.eur)});
  return f.eur_max ? money(f.eur) + '–' + money(f.eur_max) : money(f.eur);
}
function feeConditions(f) {
  const out = [];
  if (f.k === 'idle') out.push(f.from ? t('idleFromAfterEnd', {span: sessionSpan(f.from)}) : t('idleAfterEnd'));
  else if (f.from) out.push(t(['time', 'session', 'late'].includes(f.k) || f.add ? 'chargingFrom' : 'fromSpan', {span: sessionSpan(f.from)}));
  else if (f.k === 'time') out.push(t('chargingStart'));
  if (f.to) out.push(t('untilSpan', {span: sessionSpan(f.to)}));
  const when = [f.days?.length ? daysText(f.days) : null, f.hours?.length ? hoursText(f.hours) : null].filter(Boolean).join(' ');
  if (when) out.push(when);
  if (f.else) out.push(t('otherTimes'));
  if (f.some_hours) out.push(t('certainHours'));
  if (f.varies) out.push(t(({duration: 'variesDuration', time: 'variesTime', days: 'variesDays', time_days: 'variesTimeDays', multiple: 'variesMultiple'})[f.varies] || 'variesMultiple'));
  if (f.cap) out.push(t('capAmount', {amount: currency(f.cap)}));
  // Clock windows may wrap at their separating spaces, never inside a clock or its range.
  return out.map(text => keepValues(text).replace(/\u00a0(?=\d{2}:)/g, ' ')
    .replace(/(\d{2}):(\d{2})–(\d{2}):(\d{2})/g, '$1\u2060:$2\u2060–\u2060$3\u2060:$4')).join(' · ');
}
// -> [{kind, label, amount, note}] | null. Amounts and their units stay together.
// An energy surcharge (add) is its own row next to the energy price, not one of its alternatives.
export function priceRows(price) {
  const fees = feesOf(price);
  if (!fees.length) return null;
  const rows = fees.filter(f => FEES[f.k]).map(f => ({kind: f.add ? 'surcharge' : f.k, label: t(FEES[f.k]), amount: glue(feeAmount(f)), note: feeConditions(f)}));
  if (price.fees_more) rows.push({kind: 'more', label: t('moreFees', {n: price.fees_more}), amount: '', note: ''});
  if (price.session_min) rows.push({kind: 'session_min', label: t('feeSessionMin'), amount: glue(currency(price.session_min)), note: ''});
  if (price.session_max) rows.push({kind: 'session_max', label: t('feeSessionMax'), amount: glue(currency(price.session_max)), note: ''});
  return rows.length ? rows : null;
}
// Older callers: a flat list of parts.
export function priceText(price) {
  if (!price?.known) return [];
  const {main, extra} = priceParts(price);
  const parts = [main, ...extra].filter(Boolean);
  if (parts.length && price.known < price.total) parts.push(t('pricePartial'));
  return parts;
}
// Same rules as w2c/prices.summarize, over the per-point prices of one connector group (legacy keys).
export function mergePrices(list) {
  const priced = list.filter(Boolean), kwh = priced.map(p => p.kwh).filter(Boolean);
  const minute = new Map();
  for (const p of priced) for (const [from, eur] of p.minute || []) minute.set(from + '|' + eur, [from, eur]);
  const base = priced.map(p => p.base).filter(Boolean);
  return {kwh: kwh.length ? [Math.min(...kwh.map(k => k[0])), Math.max(...kwh.map(k => k[1]))] : null,
    minute: [...minute.values()].sort((a, b) => a[0] - b[0] || a[1] - b[1]), base: base.length ? Math.max(...base) : null,
    known: priced.length, total: list.length};
}
// Actual reception age is activity evidence, not a device-connectivity or occupancy verdict.
export function freshness(newest, now, staleDays, allUnknown, change = null) {
  if (!Number.isFinite(Date.parse(newest))) return {kind: 'none', text: S.noUpdate, age: null};
  const age = ageText(newest, now), minutes = (now - Date.parse(newest)) / 60000;
  if (minutes > staleDays * 1440) return {kind: 'stale', text: t('lastReport', {age}), age};
  const kind = minutes <= LIVE_MINUTES ? 'live' : 'recent';
  return {kind, text: t('lastReport', {age}), age};
}
// Newest actual reception of members, including explicit unknown reports.
// This is activity evidence, never a claim that all members are usable.
export function aggregateFreshness(points, now, staleDays) {
  const newest = points.map(p => p.received_at).filter(at => Number.isFinite(Date.parse(at)))
    .sort((a,b) => Date.parse(a)-Date.parse(b)).at(-1) ?? null;
  return freshness(newest, now, staleDays, false);
}
export function distribution(counts) {
  return ORDER.filter(state => counts[state]).map(state => ({state, n: counts[state], text: t(COUNT_KEY[state], {n: number(counts[state])})}));
}
// Right side of a group card: "2 of 4 available", or the one state that applies to all.
export function groupStatus(c, total) {
  const knownCount = total - c.unknown;
  if (!knownCount) return {tone: 'unknown', text: t('freeShort', {free:'?', known:number(total)})};
  if (c.out === total) return {tone: 'out', text: S.out};
  return {tone: c.free ? 'free' : 'busy', text: S.freeOf(c.free, total), short: t('freeShort', {free: number(c.free), known: number(total)})};
}
// Display only the independently stored own-reception observation duration.
function pointChange(p) {
  if (known(p.unchanged_since)) return {at: p.unchanged_since, exact: false};
  return null;
}
export function sinceText(change, now) {
  if (!change || !Number.isFinite(Date.parse(change.at))) return null;
  const minutes = Math.max(0, (now - Date.parse(change.at)) / 60000);
  if (minutes < 1) return t('pointSinceNow');
  // Lower bounds must not round upwards and claim more elapsed time than observed.
  const rounded = change.exact ? minutes : minutes < 60 ? Math.floor(minutes)
    : minutes < 1440 ? Math.floor(minutes / 60) * 60 : Math.floor(minutes / 1440) * 1440;
  const span = spanText(rounded);
  return t(change.exact ? 'pointSince' : 'pointSinceOver', {span});
}

export function detailModel(d, {now = Date.now(), position = null, staleDays = 7} = {}) {
  const perPoint = (d.points || []).some(p => 'price' in p);
  const zone = known(d.tz) ? d.tz : undefined;  // instants read in the station's zone, like its tariff clocks
  const points = (d.points || []).map(p => {
    const kw = typeof p.power_kw === 'number' ? p.power_kw : null;
    const current = pointCurrent(p.current, kw);
    const plugs = Array.isArray(p.connectors) && p.connectors.length ? p.connectors : [];
    const state = STATE[p.status] ?? 'unknown';
    return {state, kw, current, plugs, statusKnown: p.status_known ?? state !== 'unknown', statusUsable: p.status_usable ?? state !== 'unknown', price: p.price ?? null, received_at: p.receipt_usable !== false && known(p.received_at) ? p.received_at : null,
      timeInfo: t('pointTimestampInfo', {received: known(p.received_at) ? date(p.received_at, zone) : S.noUpdate, provider: known(p.upstream_updated_at) ? date(p.upstream_updated_at, zone) : S.noUpdate}), reported_at: known(p.reported_at) ? p.reported_at : null,
      evse: known(p.evse_id) ? String(p.evse_id) : null, change: state === 'unknown' ? null : pointChange(p)};
  });
  // Rows for an opened group: the EVSE id as delivered (AFIR), else an honest running number within the site.
  points.forEach((p, i) => { p.label = p.evse ?? t('pointN', {n: number(i + 1)}); p.stateText = t(STATE_KEY[p.state]); });
  const count = list => Object.fromEntries(ORDER.map(s => [s, list.filter(p => p.state === s).length]));
  // Group key: connectors (any order), power, AC/DC and the price, so different prices never merge.
  const groups = new Map();
  for (const p of points) {
    const priceKey = perPoint ? JSON.stringify(p.price) : '';
    const key = [[...p.plugs].sort().join('/'), p.kw, p.current, priceKey].join('|');
    if (!groups.has(key)) groups.set(key, {plugs: p.plugs, kw: p.kw, current: p.current, priceKey, members: []});
    groups.get(key).members.push(p);
  }
  const plugGroups = [...groups.values()].map(({members, ...g}) => {
    const counts = count(members), parts = perPoint ? priceParts(members[0].price) : {main: null, extra: []};
    const status = groupStatus(counts, members.length);
    const fresh = aggregateFreshness(members, now, staleDays);
    return {...g, fresh, name: g.plugs.length ? g.plugs.map(plugName).join(' / ') : S.plugUnknown, total: members.length, counts, tone: tone(counts),
      status: status.text, statusShort: status.short ?? status.text, statusTone: status.tone, priceRows: perPoint ? priceRows(members[0].price) : null, distribution: distribution(counts), price: parts.main, priceExtra: parts.extra.join('\u00a0· ') || null,
      points: members.map(m => ({label: m.label, id: m.evse, state: m.state, text: m.stateText, since: sinceText(m.change, now), timeInfo: m.timeInfo, fresh: aggregateFreshness([m], now, staleDays)}))};
  }).sort((a, b) => (b.kw ?? -1) - (a.kw ?? -1) || a.name.localeCompare(b.name)
    || Number(a.price === null) - Number(b.price === null) || String(a.price ?? '').localeCompare(String(b.price ?? '')));
  const counts = count(points), total = points.length;
  const newest = known(d.updated_at) ? d.updated_at : null;
  const address = known(d.address) ? d.address : null;
  // The server joins street and 'postcode city' with ', '; streets may contain commas themselves.
  const text = String(address ?? d.name ?? ''), cut = address ? text.lastIndexOf(', ') : -1;
  const street = cut > 0 ? text.slice(0, cut) : text, rest = cut > 0 ? [text.slice(cut + 2)] : [];
  const sitePrice = perPoint ? [] : priceText(d.price);
  const meters = position ? distance(position, [d.lat, d.lon]) : d.distance_m;
  const opening = known(d.opening) ? d.opening : null;
  const city = rest.length ? rest[0].replace(/^\d{4,5}\s+/, '') || null : null;
  const away = distanceText(meters);
  return {
    // The sheet title is street-only; preserve the old full share title independently.
    id: d.id ?? null, title: street || S.sheetTitle.details, shareTitle: street && city ? street + ', ' + city : street || S.sheetTitle.details,
    place: rest.join(', ') || null, city, address,
    operator: known(d.operator) ? d.operator : null, source: known(d.source) && d.source !== d.operator ? d.source : null,
    registry: d.source_kind === 'registry' ? {asOf: d.registry_as_of, stale: d.registry_stale, access: d.access, attribution: d.attribution} : null,
    distance: away, distanceAway: away ? t('distanceAway', {distance: away}) : null, allDay: opening === '24/7', opening,
    // Only exceptions reach the head: known hours that are not 24/7.
    openingWarning: opening && opening !== '24/7' ? t('openingChip', {value: opening}) : null,
    counts, total, allOut: total > 0 && counts.out === total, groups: plugGroups,
    fresh: d.source_kind === 'registry' ? {kind: 'static', text: t('registryChip'), age: null} : freshness(newest, now, staleDays, false),
    reported: newest ? date(newest, zone) : null,
    sitePrice: sitePrice[0] ?? null, sitePriceExtra: sitePrice.slice(1).join(', ') || null, perPointPrices: perPoint,
    route: `https://www.google.com/maps/dir/?api=1&destination=${d.lat},${d.lon}`,
  };
}
